import { makeAutoObservable, runInAction } from 'mobx';
import { v4 as uuid } from 'uuid';

import type { CartCalculationOrderResponse } from '~/api/Catalog';
import type { DeliverySlot } from '~/api/ETA';
import { DeliveryMethods } from '~/api/ETA';
import { company } from '~/company/Company';
import { cartStore } from '~/stores';
import type Delivery from '~/stores/CheckoutStore/Delivery';
import type { CheckoutStorePartial } from '~/stores/CheckoutStore/interfaces';
import type { BaseStore } from '~/stores/interfaces/BaseStore';

class SlotSelectModalStore implements BaseStore {
  private readonly _checkoutStore: CheckoutStorePartial;

  private _selectedSlot: Delivery | null = null;
  private _selectedOrder: CartCalculationOrderResponse | null = null;

  private _selectedSlotIdForSplit: string | null = null;
  private _isAutomatic: boolean = true;

  private _isLoading: boolean = false;

  /**
   * When set, the slot-select modal is operating in V3 (LMS) context for the
   * given warehouse_id; submit goes through `cartStore.selectV3Slot`
   * instead of mutating legacy `checkoutStore.deliveries`.
   */
  private _v3WarehouseId: string | null = null;

  constructor(checkoutStore: CheckoutStorePartial) {
    this._checkoutStore = checkoutStore;

    makeAutoObservable(this);
  }

  get selectedSlot(): Delivery | null {
    return this._selectedSlot;
  }

  get selectedOrder() {
    return this._selectedOrder;
  }

  get selectedSlotIdForSplit() {
    return this._selectedSlotIdForSplit;
  }

  get isAutomatic() {
    return this._isAutomatic;
  }

  get isLoading() {
    return this._isLoading;
  }

  get v3WarehouseId() {
    return this._v3WarehouseId;
  }

  setV3Context(warehouseId: string | null) {
    this._v3WarehouseId = warehouseId;
  }

  setSelectedSlot(
    slot: Delivery | null,
    order: CartCalculationOrderResponse | null,
  ): void {
    this._selectedSlot = slot;
    this._selectedOrder = order;
  }

  setSelectedSlotForSplit(slotId: string) {
    this._selectedSlotIdForSplit = slotId;
  }

  setIsAutomatic(isAutomatic: boolean) {
    this._isAutomatic = isAutomatic;
  }

  setIsLoading(isLoading: boolean) {
    this._isLoading = isLoading;
  }

  resetSelectedSlotForSplit() {
    this._selectedSlotIdForSplit = null;
  }

  removeAutomaticDelivery(selectedSlotId?: string) {
    const { deliveries } = this._checkoutStore;
    const currentDelivery = this.selectedSlot;

    if (!deliveries || !currentDelivery) {
      throw new Error('Something went wrong');
    }

    const newDeliveries = deliveries.map((delivery) => {
      if (delivery.slotDeliveryDetails?.scheduleSlotId === selectedSlotId) {
        return {
          ...delivery,
          slotDeliveryDetails: delivery.slotDeliveryDetails
            ? {
                ...delivery.slotDeliveryDetails,
                isAutomatic: false,
              }
            : undefined,
        };
      }
      return delivery;
    });

    this._checkoutStore.setDeliveries(newDeliveries);
    // Confirming the currently selected slot is the customer's own action, not a
    // backend-driven change: resync the baseline so it cannot be reported as one.
    this._checkoutStore.slotsChangedEventStore.preserveDeliveryInfo();
  }

  updateDelivery(
    selectedSlotId?: string,
    selectedDate?: string,
    isAutomatic?: boolean,
  ) {
    this.setIsLoading(true);

    const { deliveries } = this._checkoutStore;
    const currentDelivery = this.selectedSlot;

    if (!deliveries || !currentDelivery) {
      throw new Error('Something went wrong');
    }

    const currentIndex = deliveries.findIndex(
      (item) => item.id === currentDelivery.id,
    );

    const deliveryIndexToUpdate = deliveries.findIndex(
      ({ slotDeliveryDetails }) =>
        slotDeliveryDetails &&
        selectedDate &&
        slotDeliveryDetails.scheduleSlotId === selectedSlotId &&
        slotDeliveryDetails.currentDate === +selectedDate / 1000,
    );

    const newDeliveries = this._getDeliveries(
      deliveryIndexToUpdate,
      selectedSlotId,
      selectedDate,
      isAutomatic,
    );

    newDeliveries.splice(currentIndex, 1);
    newDeliveries.sort((a, b) => {
      const dateA = a.slotDeliveryDetails?.currentDate;
      const dateB = b.slotDeliveryDetails?.currentDate;

      if (!dateA && !dateB) {
        return 0;
      }

      if (!dateA) {
        return -1;
      }

      if (!dateB) {
        return 1;
      }

      return dateA - dateB;
    });

    const keyBeforeUpdate = cartStore.calculateKey;
    let switchedDeliverTogether = false;

    runInAction(() => {
      if (
        company.isExpressDeliveryOffByDefault &&
        newDeliveries.length === 1 &&
        !this._checkoutStore.isDeliverTogether &&
        // A pickup order is always a single delivery: the deliver-together shortcut
        // would discard the customer's pickup-slot pick and loop the automatic slot.
        this._checkoutStore.deliveryMethod !== DeliveryMethods.CLICK_AND_COLLECT
      ) {
        this._checkoutStore.switchDeliverTogether();
        this.setIsAutomatic(false);
        switchedDeliverTogether = true;
      } else {
        this._checkoutStore.setDeliveries(newDeliveries);
      }
    });

    // A slot picked here is the customer's own choice, not a backend-driven change:
    // resync the "delivery terms changed" baseline immediately, or the next
    // unrelated availability flip would report this pick as a change the customer
    // never made. `switchDeliverTogether` only flips a flag; the reaction that
    // rebuilds deliveries from it does not run until `updateDelivery` itself
    // returns (it is an action too, so the reaction is deferred to the end of
    // this action), so resyncing here would read the stale pre-split snapshot.
    // The `isDeliverTogether` reaction resyncs for that branch once the rebuild
    // it triggers has actually happened.
    if (!switchedDeliverTogether) {
      this._checkoutStore.slotsChangedEventStore.preserveDeliveryInfo();
    }

    // The flag raised at the top is lowered by the reaction watching the cart
    // recalculation, so it depends on one actually starting. Changing the
    // deliveries only starts one when it changes what the query is keyed on,
    // and picking the slot that was already pre-selected does not — which left
    // the checkout a skeleton behind a dead order button until a reload.
    // `switchDeliverTogether` refetches by itself, so only the other branch
    // has to be checked.
    const recalculationStarted =
      switchedDeliverTogether ||
      (cartStore.isCalculateEnabled &&
        cartStore.calculateKey !== keyBeforeUpdate);

    if (!recalculationStarted) {
      this.setIsLoading(false);
    }
  }

  private _getDeliveries(
    indexToUpdate: number,
    selectedSlotId?: string,
    selectedDate?: string,
    isAutomatic?: boolean,
  ): Delivery[] {
    const { deliveries } = this._checkoutStore;
    const currentDelivery = this.selectedSlot;

    if (!deliveries || !currentDelivery) {
      return [];
    }

    if (indexToUpdate >= 0) {
      return deliveries.map((delivery, index) => {
        if (index === indexToUpdate) {
          return {
            ...delivery,
            itemsIds: delivery.itemsIds.concat(currentDelivery.itemsIds),
          };
        }

        return delivery;
      });
    }

    const selectedSlotDeliveryDetails =
      selectedSlotId && selectedDate
        ? {
            scheduleSlotId: selectedSlotId,
            currentDate: +selectedDate / 1000,
            isAutomatic,
          }
        : undefined;
    const lastSelectedSlot = this.getSlotByIdAndDate(
      selectedSlotDeliveryDetails?.scheduleSlotId,
      selectedSlotDeliveryDetails?.currentDate
        ? `${selectedSlotDeliveryDetails?.currentDate}`
        : '',
    );

    return [
      ...deliveries,
      {
        id: uuid(),
        itemsIds: currentDelivery.itemsIds,
        slotDeliveryDetails: selectedSlotDeliveryDetails,
        slot: lastSelectedSlot,
        method: currentDelivery.method,
      },
    ];
  }

  getSlotByIdAndDate(
    slotId?: string | null,
    slotDate?: string | null,
  ): DeliverySlot | undefined {
    return slotId && slotDate
      ? this._checkoutStore.sortedDeliverySlotsList?.find(
          ({ schedule_slot_id, current_date }) =>
            schedule_slot_id === slotId && `${current_date}` === slotDate,
        )
      : undefined;
  }

  destroy(): void {
    // Clean up any resources or listeners here if necessary in future
    // Currently, no resources need to be explicitly released
  }
}

export default SlotSelectModalStore;
